import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { ModalSubtitle, ModalTitle, ModalIcon, ModalTitleWrapper, EllipsisWrapper, InviteIconWrapper, InviteText, InviteWrapper } from "./styled";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import { Badge, Box, Flex, Spinner } from "theme-ui";

import UserContext from "context/User";

import { getCompanies, createCompany, updateCompany, archiveCompany, signInCompany, resendCompanyInvite } from "api/Companies";

import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import Span from "components/typography/Span";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import PlusIcon from "components/svg/icons/PlusIcon";
import CompanyIcon from "components/svg/icons/CompanyIcon";
import { CheckMarkIcon } from "components/svg";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import TrashIcon from "components/svg/icons/TrashIcon";
import { QuestionMarkIcon } from "components/svg";
import PlaneIcon from "components/svg/icons/PlaneIcon";
import { resetLoginModal } from "helpers/loginModal";

export default function Companies() {
  const { setUser } = useContext<any>(UserContext);
  const navigate = useNavigate();

  const columnHelper = createColumnHelper();
  const [refreshData, setRefreshData] = useState<boolean>(false);  

  const [edit, setEdit] = useState<any>({});  
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [createModal, toggleCreateModal] = useState<boolean>(false);

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const [switchingLogin, setSwitchingLogin] = useState<boolean>(false);
  const [inviteText, setInviteText] = useState<string>('Invite Sent');  
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const toast = useToast();

  const columns = [
    columnHelper.accessor((row: any) => row.name, {
      id: "name",
      cell: (info: any) => (
        <div>
          <Span color="#000" fontWeight="500">{info.getValue()}</Span>
          <Span color="var(--theme-ui-colors-base_500)" display="block">{info.row.original?.owner?.email}</Span>
        </div>
      ),
      header: () => <span>Company Info</span>,
      meta: {
        style: { alignItems: 'flex-start' },
      },
    }),
    columnHelper.accessor((row: any) => row.status, {
      id: "status",
      cell: (info) => (
        <div>
          {
            info.getValue() === 0 ? (
              <InviteWrapper
              onMouseEnter={() => {
                if (!isProcessing) setInviteText("Resend Invite");
              }}
              onMouseLeave={() => {
                if (!isProcessing) setInviteText("Invite Sent");
              }}
                onClick={() => resendInvite(info.row.original)}
              >
                <Badge as="span" variant="info">
                  <InviteText>{inviteText}</InviteText>
                  <InviteIconWrapper>
                    {!isProcessing ? (
                      <PlaneIcon fill="#104E9A" width="14px" />
                    ) : (
                      <Spinner sx={{color: "#104E9A"}} size={14} />            
                    )}
                  </InviteIconWrapper>
                </Badge>
              </InviteWrapper>
            ) : null
          }
          {info.getValue() === 1 ? <Badge as="span" variant="success">Active</Badge> : null}
          {info.getValue() === 2 ? <Badge as="span" variant="danger">Inactive</Badge> : null}
        </div>
      ),
      enableSorting : false,
      header: () => <span>Status</span>,
      meta: {
        style: { width: '200px' },
      },
    }), 
    columnHelper.accessor((row: any) => row.createdAt, {
      id: "createdAt",
      cell: (info: any) => (
        <div>
          {moment(info.getValue()).format("MMMM DD, YYYY")}
        </div>
      ),
      header: () => <span>Date Added</span>,
      meta: {
        style: { width: '200px' },
      },
    }), 
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        // Determine if "Deactivate" should be included based on row status
        const items = [
          { label: "Edit", onClick: () => openEditModal(info.row.original) },
          ...(info.row.original.status === 1
            ? [
                {
                  label: "Deactivate",
                  style: { color: themevals.colors.danger },
                  onClick: () => submitArchive(info.row.original),
                },
              ]
            : []),
        ];
    
        return (
          <EllipsisWrapper>
            <DropDownMenu
              toggleComponent={
                <EllipsisIcon fill={"#818181"} width="20px" height="20px" />
              }
              items={items}
            />
          </EllipsisWrapper>
        );
      },
      header: () => <span></span>,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];               

  const openEditModal = (edit: any) => {
    setEdit(edit);
    setEditModalOpen(true);
  };

  
  const closeEditModal = () => {
    setEdit(null);
    setEditModalOpen(false);
  }; 

  const resendInvite = async (company: any) => {
    setIsProcessing(true);
    setInviteText("Resend Invite");
    const res = await resendCompanyInvite({ data: company });
    if(res?.success){ 
      toast.add("Invite resent.","var(--theme-ui-colors-success)");
      setInviteText("Invite Sent");
      setIsProcessing(false);
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);          
    }else{
      toast.add(res?.message ? res.message : "Error resending invite.","var(--theme-ui-colors-danger)");
    }          
  }

  const submitUpdate = async (values: any, actions: any) => {   
    const update = {
      name : values.name,  
      owner : {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      }   
    }  
    
    const res = await updateCompany({ data: edit, update });
    if(res?.id){ 
      toast.add("Company updated.","var(--theme-ui-colors-success)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);          
    }else{
      toast.add(res?.message ? res.message : "Error updating company","var(--theme-ui-colors-danger)");
    }          
    closeEditModal();        
  };  

  const UpdateSchema = Yup.object().shape({
    name: Yup.string().required("This field is required."),    
  });

  const toggleCreate = () => toggleCreateModal(!createModal);

  const submitCreate = async (values: any, { setErrors}: any) => {
  
    const create = {
      name: values.name,
      owner : {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      }
    };
    
    const res = await createCompany({ data: create });
    if(res?.id){ 
      toast.add("Company created","var(--theme-ui-colors-success)");
      toggleCreateModal(false);
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);          
    }else{
      toast.add(res?.message ? res.message : "Error creating company","var(--theme-ui-colors-danger)");
    }    
  };  

  const CreateSchema = Yup.object().shape({
    name: Yup.string().required("This field is required."),
    firstName: Yup.string().required("This field is required."),
    lastName: Yup.string().required("This field is required."),
    email: Yup.string().required("This field is required."),
  });


  const confirmDelete = async (company:any) => { 
    setConfirmSubmit(true); 
    const res = await archiveCompany({ data: company });
    if(res?.success){ 
      toast.add(res?.message ? res.message : "Company deactivated.","var(--theme-ui-colors-success)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      toast.add(res?.message ? res.message : "Error deactivating company.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  

  const submitArchive = async (company?: any) => {
    const targetCompany = company || edit;
    setEditModalOpen(false);  
    setConfirmModal(
    <BasicModal 
      title="Deactivate company?" 
      cancel_click={() => setConfirmModal(false)} 
      cancel_text="No, cancel"
      submitting={confirmSubmit}
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      actions={<BasicButton
        type="button"    
        scheme="danger"      
        onClick={() => confirmDelete(targetCompany)} 
        $submitting={confirmSubmit}
        $iconHeight="14px"
        $iconWidth="14px"
        $loadingColor="#fff"
        keepText={true}
      >
        {!confirmSubmit &&
        <TrashIcon fill={"#fff"}/>
       }
        
        {"Deactivate"}</BasicButton>}
    >
      <P>Are you sure you want to deactivate this company?</P>
    </BasicModal>);
  }

  const onSignIn = async () => {
    setSwitchingLogin(true);
    const res = await signInCompany({ data: edit });
    if (res?.success) {      
      toast.add("Switching to company login" ,"var(--theme-ui-colors-success)");
      resetLoginModal(); // the company login gets its own pop-up slot
      setUser({...res?.user })
      localStorage.setItem("user", JSON.stringify({user_id : res.user.id, tokens : {...res.tokens}}));
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.tokens.accessToken}`;      
      navigate("/");
    } else {
      toast.add(res?.message ? res.message : "Error loging in, please try again later" ,"var(--theme-ui-colors-red)");
    }
    setSwitchingLogin(false);
    return;
  };

  useEffect(() => {    
    document.title = process.env.REACT_APP_NAME + " | Companies"        
  }, []); 

  return (
    <>
      <Header></Header>      
      <Page title="Companies" subtitle="Manage company access.">                
        <TableData 
          api={getCompanies} 
          columns={columns} 
          refresh={refreshData} 
          actions={[<BasicButton key="1" onClick={toggleCreate} $iconHeight="10px"><PlusIcon fill="#fff"/>Add Company</BasicButton>]} 
          title={"All Companies"}
        />
      </Page>

      <Modal isOpen={editModalOpen} toggle={closeEditModal} centered={true} size="sm">
        <Formik initialValues={{
          ...(edit || {}), 
          firstName : edit?.owner?.firstName,
          lastName : edit?.owner?.lastName,
          email : edit?.owner?.email
        }} onSubmit={submitUpdate} validationSchema={UpdateSchema} >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {              
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={closeEditModal}><ModalIcon><CompanyIcon fill={"000"}/></ModalIcon><ModalTitleWrapper><ModalTitle>Edit company</ModalTitle><ModalSubtitle>Edit a companies details.</ModalSubtitle></ModalTitleWrapper></ModalHeader>
                <ModalBody>
                  <TextInput 
                    name="name"
                    label="Company Name"
                    value={values.name}
                    onChange={(val: any) => setFieldValue("name",val)}
                    $errors={errors.name && submitCount > 0 ? errors.name : null}                                    
                  />     
                  <TextInput 
                    name="firstName"
                    label="Account Owner First Name"
                    value={values.firstName}
                    onChange={(val: any) => setFieldValue("firstName",val)}
                    $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}                                    
                  />  
                  <TextInput 
                    name="lastName"
                    label="Account Owner Last Name"
                    value={values.lastName}
                    onChange={(val: any) => setFieldValue("lastName",val)}
                    $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}                                    
                  /> 
                  <TextInput 
                    name="email"
                    label="Account Owner Email"
                    value={values.email}
                    onChange={(val: any) => setFieldValue("email",val)}
                    $errors={errors.email && submitCount > 0 ? errors.email : null}                                    
                  />       
                  {/* <hr />
                  <BasicButton 
                    scheme="info"
                    styles={{margin:".25rem 0.625rem .25rem 0"}}                    
                    type="button" 
                    onClick={onSignIn} 
                    $submitting={switchingLogin}
                  >Sign In To Company</BasicButton> */}
                </ModalBody>
                <ModalFooter>
                  <Flex sx={{display : "flex", flexGrow: 1, justifyContent: "space-between"}}>
                    <BasicButton 
                      scheme="danger"
                      styles={{margin:".25rem 0.625rem .25rem 0"}}
                      outline={true}
                      type="button" 
                      onClick={() => submitArchive()} 
                      $iconHeight="14px"
                      $iconWidth="14px"
                      $loadingColor="#fff"
                    ><TrashIcon fill={"#A60000"}/>Deactivate</BasicButton> 
                    <Flex>
                      <BasicButton 
                        outline={true}               
                        styles={{margin:".25rem 20px .25rem 0"}}
                        type="button" 
                        onClick={closeEditModal} 
                        scheme="secondary"
                      >Cancel</BasicButton>
                      <BasicButton 
                        styles={{margin:".25rem 0 .25rem 0"}}
                        type="submit" 
                        $submitting={isSubmitting}
                        $iconHeight="14px"
                        $iconWidth="14px"
                        $loadingColor="#fff"
                        keepText={true}
                      >{!isSubmitting && <CheckMarkIcon fill={"#fff"}/>} Save Company</BasicButton>
                    </Flex>  
                  </Flex>                  
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal> 
      <Modal isOpen={createModal} toggle={toggleCreate} centered={true} size="sm">
        <Formik initialValues={{}} onSubmit={submitCreate} validationSchema={CreateSchema} >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            return (              
            <Form>
              <ModalHeader toggle={toggleCreate}><ModalIcon><CompanyIcon fill={"000"}/></ModalIcon><ModalTitleWrapper><ModalTitle>Add company</ModalTitle><ModalSubtitle>Add a new company with their details.</ModalSubtitle></ModalTitleWrapper></ModalHeader>
              <ModalBody>                   
                <TextInput 
                  name="name"
                  label="Company Name"
                  value={values.name}
                  onChange={(val: any) => setFieldValue("name",val)}
                  $errors={errors.name && submitCount > 0 ? errors.name : null}                                    
                />     
                <TextInput 
                  name="firstName"
                  label="Account Owner First Name"
                  value={values.firstName}
                  onChange={(val: any) => setFieldValue("firstName",val)}
                  $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}                                    
                />  
                <TextInput 
                  name="lastName"
                  label="Account Owner Last Name"
                  value={values.lastName}
                  onChange={(val: any) => setFieldValue("lastName",val)}
                  $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}                                    
                /> 
                <TextInput 
                  name="email"
                  label="Account Owner Email"
                  value={values.email}
                  onChange={(val: any) => setFieldValue("email",val)}
                  $errors={errors.email && submitCount > 0 ? errors.email : null}                                    
                />               
              </ModalBody>
              <ModalFooter>
                <>
                  <BasicButton 
                    outline={true}               
                    styles={{margin:".25rem 10px .25rem 0"}}
                    type="button" 
                    onClick={toggleCreate} 
                    scheme="secondary"
                  >Cancel</BasicButton>
                  <BasicButton 
                    styles={{margin:".25rem 0 .25rem 0"}}
                    type="submit" 
                    $submitting={isSubmitting}
                    $iconHeight="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  > {!isSubmitting &&
                    <CheckMarkIcon fill={"#fff"}/>
                    }Save Company</BasicButton>                
                </>
              </ModalFooter>
            </Form>
          );
        }}            
        </Formik>
      </Modal> 
      {confirmModal}            
    </>
  );
}
